import React from "react";
import { Sparkles } from "lucide-react";

interface AIProcessingOverlayProps {
  isVisible: boolean;
  title?: string;
  subtitle?: string;
  /**
   * 0..1 (optional). Used to gently speed up the animation as work progresses.
   * Safe to omit.
   */
  progress?: number;
}

export const AIProcessingOverlay: React.FC<AIProcessingOverlayProps> = ({
  isVisible,
  title = "AI Processing",
  subtitle = "Analyzing Content...",
  progress,
}) => {
  const gooId = React.useId();

  // Lock page scroll while the overlay is visible
  React.useEffect(() => {
    if (!isVisible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isVisible]);

  const p = Number.isFinite(progress as number) ? (progress as number) : 0;
  const clamped = Math.min(1, Math.max(0, p));
  const rate = 0.85 + clamped * 0.65; // 0.85 -> 1.50

  if (!isVisible) return null;

  return (
    <div
      className="
        absolute inset-0 z-50 flex items-center justify-center
        bg-white/35 dark:bg-black/65
        backdrop-blur-md
        transition-all duration-500
        animate-in fade-in
      "
    >
      {/* SVG filter: gooey/liquid merge */}
      <svg className="absolute w-0 h-0" aria-hidden="true" focusable="false">
        <filter id={gooId}>
          <feGaussianBlur in="SourceGraphic" stdDeviation="14" result="blur" />
          <feColorMatrix
            in="blur"
            mode="matrix"
            values="
              1 0 0 0 0
              0 1 0 0 0
              0 0 1 0 0
              0 0 0 22 -10"
            result="goo"
          />
          <feComposite in="SourceGraphic" in2="goo" operator="atop" />
        </filter>
      </svg>

      <div
        className="
          relative flex items-center justify-center
          w-64 h-64
          sm:w-72 sm:h-72
          md:w-80 md:h-80
          lg:w-96 lg:h-96
        "
        style={{ ["--siri-rate" as any]: rate }}
      >
        {/* Film grain / noise */}
        <div className="siri-noise pointer-events-none absolute inset-0 rounded-full" />

        {/* Glow ring behind (depth – vibrant) */}
        <div
          className="
            absolute rounded-full
            inset-6 sm:inset-7 md:inset-8 lg:inset-10
            blur-xl opacity-90 dark:opacity-95
            bg-[radial-gradient(circle_at_30%_30%,rgba(0,160,255,0.6),transparent_50%),
                radial-gradient(circle_at_70%_70%,rgba(150,80,255,0.55),transparent_52%),
                radial-gradient(circle_at_50%_50%,rgba(0,120,255,0.4),transparent_60%)]
            animate-siri-glow
          "
        />

        {/* Liquid blobs (goo filter applied here) */}
        <div className="absolute inset-0" style={{ filter: `url(#${gooId})` }}>
          <div className="siri-blob siri-a" />
          <div className="siri-blob siri-b" />
          <div className="siri-blob siri-c" />
          <div className="siri-blob siri-d" />
        </div>

        {/* Specular sweep (optional CSS class if you added it) */}
        <div className="siri-sweep" />

        {/* Soft specular shine */}
        <div
          className="
            absolute rounded-full pointer-events-none
            inset-10 sm:inset-11 md:inset-12 lg:inset-14
            bg-[radial-gradient(circle_at_35%_30%,rgba(255,255,255,0.55),transparent_45%)]
            dark:bg-[radial-gradient(circle_at_35%_30%,rgba(255,255,255,0.22),transparent_45%)]
            blur-md opacity-70
          "
        />

        {/* Center content */}
        <div className="relative z-10 flex flex-col items-center px-4 text-center">
          <Sparkles
            className="
              w-10 h-10
              sm:w-11 sm:h-11
              md:w-12 md:h-12
              text-slate-900
              drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]
              dark:drop-shadow-[0_1px_2px_rgba(0,0,0,0.65)]
              animate-siri-icon
            "
            aria-hidden="true"
          />

          <div className="mt-5 sm:mt-6 flex flex-col items-center" role="status" aria-live="polite">
            <p
              className="
                font-bold uppercase tracking-[0.22em]
                text-[11px] sm:text-xs md:text-sm
                text-slate-900 dark:text-slate-900
                drop-shadow-[0_1px_1px_rgba(255,255,255,0.22)]
                dark:drop-shadow-[0_1px_1px_rgba(255,255,255,0.16)]
              "
            >
              {title}
            </p>

            <p
              className="
                mt-1 font-mono
                text-[10px] sm:text-[11px] md:text-xs
                text-slate-800 dark:text-slate-800
                drop-shadow-[0_1px_1px_rgba(255,255,255,0.2)]
              "
            >
              {subtitle}
              <span className="animate-pulse"> …</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
